/**
 * The public landing page. Most visitors arrive from an ad soon after a lab test, so the first
 * screen is the promise, the price and the upload button, with the pouch as proof beside them.
 * Below it, the lab map shows which results put which ingredient in the pouch and how the formula
 * changes with new labs. The notes and the price panel answer what the map does not.
 *
 * Every band shares one content edge: `GUTTER` inside a 1328px column. The hero sits in an 8px
 * frame, so its inner padding is the gutter minus that frame, which keeps its text on the same
 * vertical line as the sections below.
 */
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/layer.stylex";
import { layer } from "@lattice/tokens/layer.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { dark } from "@lattice/tokens/themes";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { PouchShot } from "./PouchShot.tsx";
import { LabMap } from "./LabMap.tsx";
import { Providers } from "./Providers.tsx";
import { Before, Notes, Price } from "./Sections.tsx";
import { SiteFooter, SiteHeader } from "./Site.tsx";
import { StickyUpload, Upload } from "./Upload.tsx";
const TWO_UP = "@media (min-width: 720px)";
const GUTTER = "clamp(20px, 4vw, 56px)";
const FRAME = "8px";

/* No-break spaces, so the price never splits across two lines. */
const PRICE = "$175\u00a0a\u00a0month";
const styles = {
  hero: {
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kaIpWk: "x2u8bby x1h6wx5f",
    kVL7Gh: "x15j5i6c",
    kT0f0o: "x11tod8u",
    kVQacm: "xb3r6kr",
    $$css: true
  }
};
function Hero() {
  return <div className="x1717udv xpqh0ve">
      <section {...stylex.props(dark, styles.hero)} aria-labelledby="hero-heading">
        <div className="x18zekx4 xvueqy4 xelt9ym x1xtxxwa x78zum5 xdt5ytf x65nank x1t7ihnh">
          <SiteHeader />
          <div id="content" tabIndex={-1} className="x1iyjqo2 xrvj5dj x1y6fwsi x16ggrzf x6s0dn4 xe1c0t1 x1ewte8j xeonxcd x125yd6a xcmmui4">
            <div className="x78zum5 xdt5ytf x15agyyk xeuugli">
              <h1 id="hero-heading" className="x1ghz6dp xkkfdps xqu410z x1ntj4s9 xu01rmg x14abtgu x1w2vvpw">
                <span className="x1lliihq">Upload your labs. </span>
                <span className="x1lliihq">Get a personalized nutrition powder.</span>
              </h1>
              <p className="x1ghz6dp x18t6ddr x1rf3zrh xm3hrcx xbz0q3o x1jty25b x1fzhlzt">A physician signs it within 24 hours. {PRICE}.</p>
              <Upload />
            </div>
            <PouchShot placement="hero" testId="hero-pouch" />
          </div>
        </div>
      </section>
    </div>;
}
export function App() {
  return <>
      <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o xvmahel">
        <a href="#content" className="x10l6tqk xsxl7o4 xu5xgo4 x9uutwo x1jyo4x1 x3i8z2n x1m3fsnl xcqm4px xzb3lse xqu410z x1bee3ir x1cq5w7s x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp">
          Skip to content
        </a>
        <main>
          <Hero />
          <div className="x18zekx4 xvueqy4 xelt9ym x1j9m8v2">
            <Providers />
          </div>
          <Before />
          <LabMap />
          <Notes />
          <Price />
        </main>
        <SiteFooter />
        <StickyUpload />
      </div>
    </>;
}